import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Mode } from '../../../../shared/models/mode.model';
import dayjs from 'dayjs';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogVitalSignComponent } from '../dialog-vital-sign/dialog-vital-sign.component';
import { exhaustMap, filter, mergeMap, startWith, take, tap } from 'rxjs/operators';
import { OPD, OPDStatus, Program, VitalSignDoc } from '../../models/check-opd.model';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Popover } from 'primeng/popover';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { AutoCompleteSelectEvent } from 'primeng/types/autocomplete';
import { PreventDoubleClickDirective } from '../../../../shared/directives/prevent-double-click.directive';
import { InputNumber } from 'primeng/inputnumber';
import { DialogDragUseComponent } from '../dialog-drag-use/dialog-drag-use.component';
import { Divider } from 'primeng/divider';
import { Router } from '@angular/router';
import { Tag } from 'primeng/tag';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../../shared/services/auth.service';
import { ROLE } from '../../../../shared/models/auth.model';
import { DrugEquipmentService } from '../../../products/services/drug-equipment.service';
import { DrugEquipment } from '../../../products/models/drug-equipment.model';
import { OPDService } from '../../services/opd.service';
import { SpinnerStateService } from '../../../../shared/services/spinner-state.service';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { catchError, EMPTY, finalize, of, Subscription } from 'rxjs';
import { DialogAlertComponent } from '../../../../shared/components/dialog-alert/dialog-alert.component';
import { DialogConfirmComponent } from '../../../../shared/components/dialog-confirm/dialog-confirm.component';
import { VitalSignService } from '../../services/vital-sign.service';
import { AppStore } from '../../../../shared/stores/app.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { InvoiceStatus } from '../../../expense/models/expense.model';
import { SearchDrugEquipmentsRequest } from '../../../products/models/label.model';

@Component({
  selector: 'app-dialog-make-opd',
  imports: [
    ReactiveFormsModule,
    DatePipe,
    Button,
    TableModule,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Popover,
    PreventDoubleClickDirective,
    AutoComplete,
    InputNumber,
    DecimalPipe,
    Divider,
    Tag,
    TranslatePipe,
    SelectComponent,
    NgClass
  ],
  templateUrl: './dialog-make-opd.component.html',
  styleUrl: './dialog-make-opd.component.scss'
})
class DialogMakeOpdComponent implements OnInit {
  listProgram: DrugEquipment[];
  form: FormGroup;
  formMedical: FormGroup;
  mode: Mode;
  title: string = 'OPD';
  isSaving: boolean = false;
  OPDData: OPD;

  Mode = Mode;
  OPDStatus = OPDStatus;

  dialogService = inject(DialogService);
  dialogRef = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  fb = inject(FormBuilder);
  router = inject(Router);
  auth = inject(AuthService);
  drugEquipmentService = inject(DrugEquipmentService);
  opdService = inject(OPDService);
  spinnerState = inject(SpinnerStateService);
  vitalSignService = inject(VitalSignService);
  appStore = inject(AppStore);
  protected readonly InvoiceStatus = InvoiceStatus;
  private loadingService$ = toObservable(this.appStore.loadingService);
  private cancelSub?: Subscription;

  get programs(): FormArray {
    return this.form.get('programs') as FormArray;
  }

  get vitalSigns(): FormArray {
    return this.form.get('vitalSigns') as FormArray;
  }

  get medicals(): FormArray {
    return this.formMedical.get('medicals') as FormArray;
  }

  ngOnInit(): void {
    if (this.config.data?.customerFullInformation) {
      this.initCreateMode();
    } else if (this.config.data?.docNo) {
      this.initEditMode(this.config.data.docNo);
    }
  }

  createProgram(data?: Program) {
    const unit = this.listProgram?.find(x => x.id === data?.id);
    const stock = unit?.prices.find(f => f.unitId === data?.unitId);
    const group = this.fb.group({
      id: data ? data.id : null,
      name: data ? data.name : null,
      amount: data ? data.amount : null,
      unitId: data ? data.unitId : null,
      unitName: data ? data.unitName : null,
      unitList: [unit ? unit.prices : []],
      drugAdministration: data ? data.drugAdministration : null,
      stock: stock ? stock.stock : null,
      properties: data ? data.properties : null,
      price: data ? data.price : null,
      discount: data ? data.discount : null,
      total: data ? data.total : null
    });

    this.listenProgramChanges(group);

    return group;
  }

  createVitalSign(data: VitalSignDoc): FormGroup {
    return this.fb.group({
      docNo: data.docNo,
      created: data.created,
      icd: [data.icd]
    });
  }

  createMedical(data: VitalSignDoc): FormGroup {
    return this.fb.group({
      docNo: data.docNo,
      mcCreated: data.mcCreated,
      mcDocNo: data.mcDocNo
    });
  }

  calculateAge() {
    if (!this.config?.data?.customerFullInformation?.birthday) {
      return '-';
    }
    const birth = dayjs(this.config.data.customerFullInformation.birthday);
    const today = dayjs();

    const years = today.diff(birth, 'year');
    const m = today.subtract(years, 'year');
    const months = m.diff(birth, 'month');
    const d = m.subtract(months, 'month');
    const days = d.diff(birth, 'day');

    return `${years} Year ${months} Month ${days} Day`;
  }

  onCancelOPD(docNo: string) {
    this.cancelSub?.unsubscribe();

    this.dialogService.open(DialogConfirmComponent, {
      header: `Doc No: ${docNo}`,
      styleClass: 'p-dialog-header text-center',
      data: {
        header: 'common.message.CONFIRM_CANCEL',
        confirm: 'common.button.yes',
        severityConfirm: 'danger',
        reject: 'common.button.cancel'
      }
    });

    this.cancelSub = this.loadingService$
      .pipe(
        filter(f => f),
        take(1),
        exhaustMap(() => {
          return this.opdService.cancelOPD(docNo).pipe(
            tap(() => {
              this.dialogService.open(DialogAlertComponent, {
                showHeader: false,
                data: {
                  gif: 'assets/images/check.gif',
                  header: 'common.message.SAVED_SUCCESSFULLY',
                  close: 'common.button.ok'
                },
                width: '320px'
              });
              this.dialogRef.close('fetch');
            }),
            catchError(() => {
              return EMPTY;
            }),
            finalize(() => {
              this.appStore.setLoadingService(false);
            })
          );
        })
      )
      .subscribe();
  }

  goVitalsSign(docNo?: string) {
    const ref = this.dialogService.open(DialogVitalSignComponent, {
      styleClass: 'p-dialog-content-footer',
      showHeader: false,
      width: '100%',
      data: {
        docNo: docNo,
        customerFullInformation: this.OPDData.customerFullInformation,
        mode: this.mode
      }
    });

    ref?.onClose.pipe(filter(f => !!f)).subscribe(res => {
      if (res === 'remove') {
        const index = this.vitalSigns.controls.findIndex((c: any) => c.get('docNo')?.value === docNo);
        if (index > -1) {
          this.vitalSigns.removeAt(index);
        }
      } else {
        const index = this.vitalSigns.controls.findIndex((c: any) => c.get('docNo')?.value === res.data.docNo);
        if (index === -1) {
          this.vitalSigns.push(this.createVitalSign(res.data));
          if (res.data.mcDocNo) {
            this.medicals.push(this.createMedical(res.data));
          }
        } else {
          this.vitalSigns.setControl(index, this.createVitalSign(res.data));
          if (res.data.mcDocNo) {
            this.medicals.setControl(index, this.createMedical(res.data));
          } else {
            this.medicals.removeAt(index);
          }
        }
      }
    });
  }

  goCancelVitalsSign(docNo: string) {
    this.cancelSub?.unsubscribe();

    this.dialogService.open(DialogConfirmComponent, {
      header: `Doc No: ${docNo}`,
      styleClass: 'p-dialog-header text-center',
      data: {
        header: 'common.message.CONFIRM_CANCEL',
        confirm: 'common.button.yes',
        severityConfirm: 'danger',
        reject: 'common.button.cancel'
      }
    });

    this.cancelSub = this.loadingService$
      .pipe(
        filter(f => f),
        take(1),
        exhaustMap(() =>
          this.vitalSignService.cancelVitalSign(docNo).pipe(
            tap(() => {
              const index = this.vitalSigns.controls.findIndex((c: any) => c.get('docNo')?.value === docNo);
              if (index > -1) {
                this.vitalSigns.removeAt(index);
              }
            }),
            catchError(() => {
              return EMPTY;
            }),
            finalize(() => {
              this.appStore.setLoadingService(false);
            })
          )
        )
      )
      .subscribe();
  }

  onAddList(event: AutoCompleteSelectEvent, index: number) {
    if (event && event.value) {
      this.programs.at(index).patchValue({
        no: index + 1,
        id: event.value.id,
        name: event.value.name,
        unitList: event.value.prices,
        drugAdministration: event.value.drugAdministration,
        properties: event.value.properties
      });

      this.programs.at(index).patchValue({
        unitId: event.value.prices[0].unitId,
        price: event.value.prices[0].price,
        stock: event.value.prices[0].stock,
        amount: 1
      });

      this.programs.push(this.createProgram());
    }
  }

  onSelectUnit(event: any, index: number, list: any[]) {
    if (event && event.value) {
      const find = list.find(f => f.unitId === event.value);

      if (!find) {
        return;
      }
      this.programs.at(index).patchValue({
        price: find.price,
        discount: null,
        stock: find.stock,
        amount: 1
      });
    }
  }

  onUpdateUse(index: number, mode = Mode.CREATE) {
    const ref = this.dialogService.open(DialogDragUseComponent, {
      showHeader: false,
      data: {
        drugAdministration: this.programs.at(index).value.drugAdministration,
        properties: this.programs.at(index).value.properties,
        mode: mode
      }
    });

    ref?.onClose.pipe(filter(f => !!f)).subscribe(res => {
      this.programs.at(index).patchValue({
        drugAdministration: res.drugAdministration,
        properties: res.properties
      });
    });
  }

  onDeleteProgram(index: number) {
    this.programs.removeAt(index);

    if (this.programs.length === 0) {
      this.programs.push(this.createProgram());
    }
  }

  onSave() {
    const payload = {
      ...this.form.getRawValue(),
      programs: this.programs.value.every((x: any) => !x.id) ? [] : this.programs.value.filter((x: any) => !!x.id),
      vitalSigns: this.vitalSigns.value.map((x: any) => x.docNo)
    };
    this.isSaving = true;
    this.opdService.saveOPD(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.dialogService
          .open(DialogAlertComponent, {
            showHeader: false,
            data: {
              gif: 'assets/images/check.gif',
              header: 'common.message.SAVED_SUCCESSFULLY',
              close: 'common.button.ok'
            },
            width: '320px'
          })
          ?.onClose.subscribe(() => this.dialogRef.close('fetch'));
      },
      error: () => {
        this.isSaving = false;
      }
    });
  }

  goPrintMC(mcDocNo: string) {
    this.vitalSignService.printMedicalCertificate(mcDocNo).subscribe(res => {
      const byteChars = atob(res.data);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      URL.revokeObjectURL(url);
    });
  }

  onClose(): void {
    this.dialogRef.close(this.mode === Mode.VIEW ? '' : 'fetch');
  }

  queryList(query?: AutoCompleteCompleteEvent) {
    if (query) {
      const request: SearchDrugEquipmentsRequest = {
        search: query.query
      };

      this.drugEquipmentService.searchProgram(request).subscribe(res => {
        this.listProgram = res.data;
      });
    }
  }

  calOPDStatus(status: OPDStatus) {
    switch (status) {
      case OPDStatus.WAITING_FOR_EXAMINING:
        return 'warn';
      case OPDStatus.EXAMINING:
        return 'info';
      case OPDStatus.WAITING_FOR_PAYMENT:
        return 'danger';
      case OPDStatus.COMPLETE:
        return 'success';
      case OPDStatus.CANCEL:
        return 'secondary';
    }
  }

  private initForm(docNo: string | null, customerId: string): void {
    this.form = this.fb.group({
      docNo: docNo,
      programs: this.fb.array([]),
      vitalSigns: this.fb.array([]),
      summary: null,
      discount: null,
      total: null,
      customerId: customerId
    });
  }

  private initFormMedical(): void {
    this.formMedical = this.fb.group({
      medicals: this.fb.array([])
    });
  }

  private initCreateMode(): void {
    this.mode = Mode.CREATE;
    this.OPDData = {
      status: OPDStatus.WAITING_FOR_EXAMINING,
      customerFullInformation: this.config.data.customerFullInformation,
      programs: [],
      vitalSigns: []
    };
    this.initForm(null, this.config.data.customerFullInformation.id);
    this.initFormMedical();
    this.setupValueChangesListeners();
    this.goVitalsSign();
    this.programs.push(this.createProgram());
  }

  private initEditMode(docNo: string): void {
    this.opdService
      .getOPDDetail(docNo)
      .pipe(
        mergeMap(res => {
          this.mode = Mode.EDIT;
          this.title = res.data.docNo!;
          this.OPDData = res.data;

          if (
            [OPDStatus.COMPLETE, OPDStatus.CANCEL].includes(this.OPDData.status) ||
            (OPDStatus.WAITING_FOR_PAYMENT === this.OPDData.status &&
              InvoiceStatus.COMPLETE === this.OPDData.invoiceStatus)
          ) {
            this.mode = Mode.VIEW;
          }

          this.initForm(docNo, this.OPDData.customerFullInformation.id);
          this.initFormMedical();
          res.data.vitalSigns.forEach(vs => {
            if (vs.mcDocNo) {
              this.medicals.push(this.createMedical(vs));
            }
            this.vitalSigns.push(this.createVitalSign(vs));
          });

          const ids = res.data.programs.map(p => p.id);
          if (ids.length === 0) {
            return of({ data: [] });
          }

          return this.drugEquipmentService.searchProgram({ ids }).pipe(
            catchError(() => {
              this.dialogRef.close();
              return EMPTY;
            })
          );
        }),
        catchError(() => {
          this.dialogRef.close();
          return EMPTY;
        })
      )
      .subscribe(listId => {
        this.listProgram = listId.data;
        this.setupValueChangesListeners();

        if (
          this.auth.getProfile().role === ROLE.DOCTOR &&
          this.OPDData.status === this.OPDStatus.WAITING_FOR_EXAMINING
        ) {
          this.OPDData.status = this.OPDStatus.EXAMINING;
          this.opdService.examining(docNo).subscribe({
            error: () => {
              this.dialogRef.close();
            }
          });
        }

        this.OPDData.programs.forEach(data => this.programs.push(this.createProgram(data)));

        if ([Mode.CREATE, Mode.EDIT].includes(this.mode)) {
          this.programs.push(this.createProgram());
        }
      });
  }

  private setupValueChangesListeners() {
    this.programs.valueChanges.pipe(startWith(this.programs.value)).subscribe(() => {
      this.calculateTotals();
    });
  }

  private calculateTotals() {
    const programList = this.programs.value || [];

    const subAll = programList.reduce((acc: number, curr: any) => {
      return acc + (Number(curr.price) * Number(curr.amount) || 0);
    }, 0);

    const subDiscount = programList.reduce((acc: number, curr: any) => {
      return acc + (Number(curr.discount) * Number(curr.amount) || 0);
    }, 0);

    const subTotal = programList.reduce((acc: number, curr: any) => {
      return acc + (Number(curr.total) || 0);
    }, 0);

    this.form.patchValue(
      {
        summary: subAll,
        discount: subDiscount,
        total: subTotal
      },
      { emitEvent: false }
    );
  }

  private listenProgramChanges(group: FormGroup) {
    const calc = () => {
      const amount = Number(group.get('amount')?.value ?? 0);
      const price = Number(group.get('price')?.value ?? 0);
      const discount = Number(group.get('discount')?.value ?? 0);
      const total = amount * (price - discount);

      const discountField = group.get('discount');
      if (price === 0) {
        discountField?.setValue(0, { emitEvent: false });
        discountField?.disable({ emitEvent: false });
      } else {
        discountField?.enable({ emitEvent: false });
      }

      group.patchValue({ total }, { emitEvent: false });
    };

    group.get('amount')?.valueChanges.subscribe(calc);
    group.get('discount')?.valueChanges.subscribe(calc);
    group.get('price')?.valueChanges.subscribe(calc);
  }
}

export default DialogMakeOpdComponent;
